import { useEffect, useRef } from 'react';
import { useUnitPowerUpdateSubscription } from '../../../types/generated-types';
import { unitPowerReadContext } from './unit-power-read';

/** Mongo subscription changes contain either dotted paths or nested changed fields. */
export function affectsUnitPowerView(changes: unknown): boolean {
  if (!changes || typeof changes !== 'object') return false;
  const relevant = [
    'powerState',
    'operatingMode',
    'operatingState',
    'setpoint',
  ];
  return Object.entries(changes).some(([key, value]) =>
    relevant.some(
      (metric) =>
        key === `${metric}.value` ||
        (key === metric &&
          !!value &&
          typeof value === 'object' &&
          !Array.isArray(value) &&
          Object.prototype.hasOwnProperty.call(value, 'value')),
    ),
  );
}

/** Only confirmed device events invalidate read-time Unit projections; never a clock. */
export function useUnitPowerEvents(
  refresh: () => Promise<unknown>,
  ids: string[],
) {
  const key = [...new Set(ids)].sort().join('\n');
  const state = useRef({ active: true, pending: false, dirty: false, key });
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    const current = { active: true, pending: false, dirty: false, key };
    state.current = current;
    return () => {
      current.active = false;
    };
  }, [key]);
  useUnitPowerUpdateSubscription({
    variables: { ids: key ? key.split('\n') : [] },
    skip: !key,
    fetchPolicy: 'no-cache',
    context: unitPowerReadContext,
    onData: ({ data }) => {
      const event = data.data?.thermostatEventsByIds;
      const current = state.current;
      const ready = event?.modelChangeOperation === 'subscriptionReady';
      if (
        !current.active ||
        current.key !== key ||
        (!ready && (!event?._id || !ids.includes(event._id))) ||
        (event?.modelChangeOperation === 'update' &&
          !affectsUnitPowerView(event.updateDescription))
      )
        return;
      current.dirty = true;
      if (current.pending) return;
      current.pending = true;
      void (async () => {
        try {
          do {
            current.dirty = false;
            await refreshRef.current();
          } while (current.active && current.dirty);
        } catch {
          // Query owns its error state; another real event may retry the read.
        } finally {
          current.pending = false;
        }
      })();
    },
  });
}
